import { useState, useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import { AlertTriangle } from 'lucide-react'

interface AutoLogoutWarningProps {
  isOpen: boolean
  onStaySignedIn: () => void
  onLogoutNow: () => void
  warningMinutes: number
}

export default function AutoLogoutWarning({
  isOpen,
  onStaySignedIn,
  onLogoutNow,
  warningMinutes
}: AutoLogoutWarningProps) {
  const [countdown, setCountdown] = useState(warningMinutes * 60) // Convert to seconds

  useEffect(() => {
    if (!isOpen) {
      setCountdown(warningMinutes * 60)
      return
    }

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Auto logout when countdown reaches 0
          onLogoutNow()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isOpen, warningMinutes, onLogoutNow])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Dialog 
      open={isOpen} 
      onClose={() => {}} // Prevent closing by clicking outside
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-6 w-6 text-amber-600" aria-hidden="true" />
            </div>
          </div>

          <div className="mt-3 text-center">
            <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
              Session Timeout Warning
            </Dialog.Title>
            
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">
                You've been inactive for a while. For your security, you'll be automatically logged out in:
              </p>
              
              <div className="text-2xl font-mono font-bold text-amber-600 mb-4">
                {formatTime(countdown)}
              </div>
              
              <p className="text-sm text-gray-500">
                Do you want to stay signed in?
              </p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors"
              onClick={onStaySignedIn}
            >
              Stay Signed In
            </button>
            
            <button
              type="button"
              className="flex-1 rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 transition-colors"
              onClick={onLogoutNow}
            >
              Logout Now
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
} 